import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { tagopsService } from '../services/tagopsService';
import { useNotifications, createErrorNotification, createSuccessNotification } from './common/NotificationSystem';
import { PermissionButton } from './common/PermissionButton';
import { format } from 'date-fns';

interface ApiToken {
  isActive: boolean;
  expiresAt: string;
  createdAt: string;
  scope: 'read' | 'write';
  name: string;
}

export const ApiTokens: React.FC = () => {
  const { showNotification } = useNotifications();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [createdTokenData, setCreatedTokenData] = useState<{
    token: string;
    name: string;
    expiresAt: string;
    scope: 'read' | 'write';
  } | null>(null);
  const [newToken, setNewToken] = useState({
    name: '',
    expiresAt: '',
    scope: 'read' as 'read' | 'write',
  });
  const [processing, setProcessing] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const response = await tagopsService.listApiTokens();
      setTokens(response.tokens || []);
    } catch (error: any) {
      showNotification(createErrorNotification(error, undefined, 'Failed to load API tokens'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    // Validation
    if (!newToken.name.trim()) {
      showNotification(createErrorNotification(new Error('Token name is required'), undefined, 'Validation Error'));
      return;
    }

    if (newToken.name.length > 255) {
      showNotification(createErrorNotification(new Error('Token name must be 255 characters or less'), undefined, 'Validation Error'));
      return;
    }

    if (!newToken.expiresAt) {
      showNotification(createErrorNotification(new Error('Expiration date is required'), undefined, 'Validation Error'));
      return;
    }

    // Check if expiration is more than 1 year from now
    const expirationDate = new Date(newToken.expiresAt);
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    
    if (expirationDate > oneYearFromNow) {
      showNotification(createErrorNotification(new Error('Expiration date cannot be more than 1 year from now'), undefined, 'Validation Error'));
      return;
    }

    // Convert expiration date to timestamp (seconds) as integer
    const expiresAtTimestamp = Math.floor(expirationDate.getTime() / 1000);

    setProcessing(new Set(['create']));
    try {
      const response = await tagopsService.createApiToken({
        name: newToken.name.trim(),
        expiresAt: expiresAtTimestamp.toString(),
        scope: newToken.scope,
      });
      
      // Store all token data from response
      setCreatedTokenData({
        token: response.token,
        name: response.name,
        expiresAt: response.expiresAt.toString(),
        scope: response.scope,
      });
      setShowCreateDialog(false);
      setShowTokenDialog(true);
      setNewToken({ name: '', expiresAt: '', scope: 'read' });
      await loadTokens();
      showNotification(createSuccessNotification('Success', 'API token created successfully'));
    } catch (error: any) {
      showNotification(createErrorNotification(error, undefined, 'Failed to create API token'));
    } finally {
      setProcessing(new Set());
    }
  };

  const handleDeleteToken = async (name: string) => {
    if (!window.confirm(`Are you sure you want to delete the token "${name}"?`)) {
      return;
    }

    setProcessing(new Set([`delete-${name}`]));
    try {
      await tagopsService.deleteApiToken(name);
      await loadTokens();
      showNotification(createSuccessNotification('Success', 'API token deleted successfully'));
    } catch (error: any) {
      showNotification(createErrorNotification(error, undefined, 'Failed to delete API token'));
    } finally {
      setProcessing(new Set());
    }
  };

  const handleActivateToken = async (name: string) => {
    setProcessing(new Set([`activate-${name}`]));
    try {
      await tagopsService.activateApiToken(name);
      await loadTokens();
      showNotification(createSuccessNotification('Success', 'API token activated successfully'));
    } catch (error: any) {
      showNotification(createErrorNotification(error, undefined, 'Failed to activate API token'));
    } finally {
      setProcessing(new Set());
    }
  };

  const handleDeactivateToken = async (name: string) => {
    setProcessing(new Set([`deactivate-${name}`]));
    try {
      await tagopsService.deactivateApiToken(name);
      await loadTokens();
      showNotification(createSuccessNotification('Success', 'API token deactivated successfully'));
    } catch (error: any) {
      showNotification(createErrorNotification(error, undefined, 'Failed to deactivate API token'));
    } finally {
      setProcessing(new Set());
    }
  };

  const handleCopyToken = () => {
    if (createdTokenData) {
      navigator.clipboard.writeText(createdTokenData.token);
      showNotification(createSuccessNotification('Copied', 'Token copied to clipboard'));
    }
  };

  const handleDownloadToken = () => {
    if (!createdTokenData) return;
    
    // Format expiration date for CSV - use ISO format to avoid comma issues
    const expirationDate = new Date(parseInt(createdTokenData.expiresAt) * 1000).toISOString();
    
    // Escape values that might contain commas or quotes
    const escapeCsvValue = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };
    
    const csvContent = `Token Name,Token Value,Expires At,Scope\n${escapeCsvValue(createdTokenData.name)},${escapeCsvValue(createdTokenData.token)},${escapeCsvValue(expirationDate)},${escapeCsvValue(createdTokenData.scope)}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-token-${createdTokenData.name}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showNotification(createSuccessNotification('Downloaded', 'Token saved as CSV'));
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(parseInt(timestamp) * 1000);
      return format(date, 'PPpp');
    } catch {
      return timestamp;
    }
  };

  const isExpired = (expiresAt: string): boolean => {
    try {
      const expirationDate = new Date(parseInt(expiresAt) * 1000);
      return expirationDate < new Date();
    } catch {
      return false;
    }
  };

  // Calculate max date (1 year from now)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateString = maxDate.toISOString().split('T')[0];

  // Calculate min date (today)
  const minDate = new Date().toISOString().split('T')[0];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          API Tokens
        </Typography>
        <PermissionButton
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateDialog(true)}
          permission="Administrator"
        >
          Create Token
        </PermissionButton>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tokens.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No API tokens found. Create your first token to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Expires At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token) => {
                const expired = isExpired(token.expiresAt);
                return (
                  <TableRow key={token.name}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {token.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={token.scope}
                        size="small"
                        color={token.scope === 'write' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {expired ? (
                        <Chip label="Expired" size="small" color="error" />
                      ) : token.isActive ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip label="Inactive" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>{formatTimestamp(token.createdAt)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={expired ? 'error' : 'text.primary'}
                      >
                        {formatTimestamp(token.expiresAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {token.isActive ? (
                          <Tooltip title="Deactivate Token">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleDeactivateToken(token.name)}
                              disabled={processing.has(`deactivate-${token.name}`)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate Token">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleActivateToken(token.name)}
                              disabled={processing.has(`activate-${token.name}`)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Token">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteToken(token.name)}
                            disabled={processing.has(`delete-${token.name}`)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Token Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create API Token</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Token Name"
              value={newToken.name}
              onChange={(e) => setNewToken({ ...newToken, name: e.target.value })}
              fullWidth
              required
              helperText={`${newToken.name.length}/255 characters`}
              error={newToken.name.length > 255}
            />
            <TextField
              label="Expiration Date"
              type="date"
              value={newToken.expiresAt}
              onChange={(e) => setNewToken({ ...newToken, expiresAt: e.target.value })}
              fullWidth
              required
              inputProps={{
                min: minDate,
                max: maxDateString,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Maximum 1 year from today"
            />
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                value={newToken.scope}
                onChange={(e) => setNewToken({ ...newToken, scope: e.target.value as 'read' | 'write' })}
                label="Scope"
              >
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="write">Write</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateToken}
            disabled={processing.has('create') || !newToken.name.trim() || !newToken.expiresAt || newToken.name.length > 255}
          >
            {processing.has('create') ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Token Display Dialog */}
      <Dialog open={showTokenDialog} onClose={() => setShowTokenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>API Token Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This token will be displayed only once. Please copy it now and store it securely.
          </Alert>
          {createdTokenData && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Token Name
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {createdTokenData.name}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scope
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {createdTokenData.scope}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Expires At
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatTimestamp(createdTokenData.expiresAt)}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Token Value
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {createdTokenData.token}
                </Box>
              </Box>
            </>
          )}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyToken}
              fullWidth
            >
              Copy Token
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadToken}
              fullWidth
            >
              Save as CSV
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShowTokenDialog(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
